import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ModalDialog } from 'components/ModalDialog';
import {
    BaseCostWorkScopesEditModal,
    type TUseUpdateWorkScopeData,
} from 'pages/Project/Common/BaseCostWorkScopesEditModal/BaseCostWorkScopesEditModal';
import type { TUseUpdatePriceWorkScope } from 'pages/Project/Common/BaseCostWorkScopesEditModal/hooks/useUpdateWorkScope';
import { Button, Tabs } from 'uikit';

import type { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useRef, useState } from 'react';

import type { ICatalogUerBaseCostScopesModal } from './helpers';
import { tabsItems } from './helpers';
import {
    useCatalogUERBaseCostWorkScopes,
    useCatalogUerBaseCostWorkScopesColumns,
    useCatalogUerBaseCostWorkScopesData,
    useUpdateCatalogUERBaseCostWorkScopes,
} from './hooks';

import Style from './CatalogUerBaseCostWorkScopesModal.module.css';

export const CatalogUerBaseCostWorkScopesModal = (props: ICatalogUerBaseCostScopesModal) => {
    const { onClose, rowData, catalogUniqueId } = props;

    const tableRef = useRef<AgGridReact>(null);
    const title = useMemo(() => `Расценка ${rowData?.code}`, [rowData?.code]);

    const [activeTab, setActiveTab] = useState(tabsItems[0]);
    const [isWorkScopesEditOpen, setIsWorkScopesEditOpen] = useState(false);

    const onTabChange = useCallback(({ value }) => setActiveTab(value), [setActiveTab]);

    const onWorkScopesEditClose = useCallback(() => {
        setIsWorkScopesEditOpen(false);
    }, []);

    const onWorkScopesEditOpen = useCallback(() => {
        setIsWorkScopesEditOpen(true);
    }, []);

    const { items, isLoading, workScopesRefresh, onFiltersUpdated, currentFilterData } =
        useCatalogUerBaseCostWorkScopesData({
            activeTabId: activeTab?.id,
            catalogUniqueId,
            uerBaseCostId: rowData?.id,
        });

    const columns = useCatalogUerBaseCostWorkScopesColumns({
        activeTabId: activeTab?.id,
        currentFilterData,
        onFiltersUpdated,
    });

    const tableUniqueId = `CatalogUerBaseCostWorkScopesModal_${activeTab.id}`;

    return (
        <>
            <ModalDialog
                isOpen
                title={title}
                onClose={onClose}
                dockedToWindow="bottom"
                withTitleCloseButton
                isOnClickOutsideDisable
                withBottomButtons={false}
            >
                <div className={Style.modalBody}>
                    <Tabs className={Style.tabs} value={activeTab} onChange={onTabChange} items={tabsItems} size="s" />
                    {activeTab === tabsItems[0] && (
                        <div className={Style.tabs}>
                            <Button label="Редактировать" size="xs" view="secondary" onClick={onWorkScopesEditOpen} />
                        </div>
                    )}
                    <div className={`${Style.gridBody} ag-theme-alpine customHeaderColor`}>
                        {activeTab === tabsItems[0] && !items.length ? (
                            <div className={Style.createWorkBody}>
                                <div className={Style.createWorkTitle}>
                                    Для отображения данных сформируйте состав работ
                                </div>
                                <Button
                                    label="Создать работу"
                                    size="xs"
                                    view="secondary"
                                    onClick={onWorkScopesEditOpen}
                                />
                            </div>
                        ) : (
                            <AgGridTable
                                ref={tableRef}
                                rowData={items as any}
                                columnDefs={columns as any}
                                overlayNoRowsTemplate="Ничего не найдено"
                                loading={isLoading}
                                context={{
                                    tableUniqueId,
                                }}
                            />
                        )}
                    </div>
                </div>
            </ModalDialog>
            {isWorkScopesEditOpen && (
                <BaseCostWorkScopesEditModal
                    onRefresh={workScopesRefresh}
                    onClose={onWorkScopesEditClose}
                    useUpdateWorkScopeData={useCatalogUERBaseCostWorkScopes as TUseUpdateWorkScopeData}
                    useUpdateWorkScopeDataParams={{
                        catalogUniqueId,
                        uerBaseCostId: rowData?.id,
                    }}
                    useUpdate={useUpdateCatalogUERBaseCostWorkScopes as TUseUpdatePriceWorkScope}
                    useUpdateParams={{
                        uerBaseCostId: rowData?.id,
                    }}
                />
            )}
        </>
    );
};

CatalogUerBaseCostWorkScopesModal.displayName = 'CatalogUerBaseCostWorkScopesModal';
